import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import { getUserAvatarConfig, buildAvatarLayers, getAllStudentAssets } from '../services/avatarBuilderService';

/**
 * AvatarLayeredPreview
 *
 * Props:
 *  - userId        {string}   required — whose avatar to load
 *  - size          {number}   optional — pixel size of the square canvas (default 120)
 *  - showEditButton {boolean} optional — show "Edit Avatar" button (default true)
 *  - onEditClick   {function} optional — override default navigate to /student-avatar-studio
 *  - className     {string}   optional — extra wrapper classes
 */
const AvatarLayeredPreview = ({
  userId,
  size = 120,
  showEditButton = true,
  onEditClick,
  className = '',
}) => {
  const navigate = useNavigate();
  const [layers, setLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAvatar, setHasAvatar] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadAvatar = async () => {
      try {
        setLoading(true);
        const config = await getUserAvatarConfig(userId);

        if (!config?.avatar_config) {
          setHasAvatar(false);
          setLayers([]);
          return;
        }

        // Load all active student assets once, group by category slug
        const allAssets = await getAllStudentAssets();
        const assetsByCategorySlug = allAssets?.reduce((acc, asset) => {
          const slug = asset?.avatar_asset_categories?.slug;
          if (!slug) return acc;
          if (!acc?.[slug]) acc[slug] = [];
          acc?.[slug]?.push(asset);
          return acc;
        }, {});

        const resolvedLayers = buildAvatarLayers(config?.avatar_config, assetsByCategorySlug);
        setLayers(resolvedLayers);
        setHasAvatar(resolvedLayers?.length > 0);
      } catch (err) {
        console.error('AvatarLayeredPreview: failed to load avatar', err);
        setHasAvatar(false);
        setLayers([]);
      } finally {
        setLoading(false);
      }
    };

    loadAvatar();
  }, [userId]);

  const handleEdit = () => {
    if (onEditClick) {
      onEditClick();
    } else {
      navigate('/student-avatar-studio');
    }
  };

  return (
    <div className={`flex flex-col items-center gap-2 ${className}`}>
      {/* Canvas */}
      <div
        className="relative rounded-xl overflow-hidden bg-muted/30 border border-border/50 flex-shrink-0"
        style={{ width: size, height: size }}
      >
        {loading ? (
          /* Loading shimmer */
          (<div className="absolute inset-0 animate-pulse bg-muted/60 flex items-center justify-center">
            <Icon name="User" size={size * 0.4} className="text-muted-foreground/40" />
          </div>)
        ) : hasAvatar ? (
          /* Layered avatar images stacked with absolute positioning */
          (layers?.map((layer, idx) => (
            <img
              key={`${layer?.slug}-${idx}`}
              src={layer?.publicUrl}
              alt={layer?.slug || layer?.category}
              className="absolute inset-0 w-full h-full object-contain"
              style={{ zIndex: layer?.layer_order ?? idx }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )))
        ) : (
          /* Placeholder silhouette */
          (<div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
            <svg
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ width: size * 0.65, height: size * 0.65, opacity: 0.25 }}
            >
              {/* Head */}
              <circle cx="50" cy="30" r="18" fill="currentColor" className="text-muted-foreground" />
              {/* Body */}
              <path
                d="M20 95 C20 68 80 68 80 95"
                fill="currentColor"
                className="text-muted-foreground"
              />
            </svg>
            <span
              className="text-muted-foreground/50 font-medium text-center leading-tight"
              style={{ fontSize: Math.max(9, size * 0.09) }}
            >
              No avatar
            </span>
          </div>)
        )}
      </div>
      {/* Edit button */}
      {showEditButton && (
        <button
          onClick={handleEdit}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 transition-colors duration-150 text-xs font-medium"
          title="Edit avatar in Avatar Studio"
        >
          <Icon name="Pencil" size={12} />
          <span>Edit Avatar</span>
        </button>
      )}
    </div>
  );
};

export default AvatarLayeredPreview;
